import { useEffect, useRef, useState } from "react";

export interface ChatNotificationPayload {
  eventType: "chat.notification.per_user" | "chat.notification.batch";
  visitId?: number;
  contextId?: string;
  recipientUserId?: string;
  senderUserId?: string;
  messageId?: number;
  unreadCount?: number;
  recipientUserIds?: string[];
  recipientUnreadCount?: Record<string, number>;
  createdAt?: string;
}

interface useChatSseOptions {
  visitId: any;
  sseUrl?: string;
  onNotification?: (payload: ChatNotificationPayload) => void;
  userId: any;
}

export const useChatSse = ({
  visitId,
  sseUrl,
  onNotification,
  userId,
}: useChatSseOptions) => {
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [messageVisitId, setMessageVisitId] = useState<any>({});
  const [error, setError] = useState<Event | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000; // 1 second

  const connect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Build URL with visit-specific topic
      const fullUrl = `${sseUrl}/chat-notification-user-${userId}-visit-${visitId}`;
      console.log("Connecting to SSE for visit:", fullUrl);

      const eventSource: any = new EventSource(fullUrl);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log("SSE connection opened for visit:", visitId);
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
      };

      eventSource.onmessage = (event: MessageEvent) => {
        try {
          const payload: any = JSON.parse(event.data);
          console.log("SSE message received for visit:", visitId, payload);
          setMessageVisitId((prev: any) => ({
            ...prev,
            [payload.contextId?.split("-")[1]?.toString()]: payload.unreadCount,
          }));

          // Handle the notification (e.g., update UI, show alert, etc.)
          if (onNotification) {
            onNotification(payload);
          }
        } catch (parseError) {
          console.error("Failed to parse SSE message:", parseError);
        }
      };

      eventSource.onerror = (event: Event) => {
        console.error("SSE connection error for visit:", visitId, event);
        setError(event);
        setIsConnected(false);

        // Attempt reconnection
        scheduleReconnect();
      };

      eventSource.onclose = () => {
        console.log("SSE connection closed for visit:", visitId);
        setIsConnected(false);
      };
    } catch (connectionError) {
      console.error("Failed to create SSE connection:", connectionError);
      scheduleReconnect();
    }
  };

  const scheduleReconnect = () => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error("Max reconnection attempts reached for visit:", visitId);
      return;
    }

    const delay =
      baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
    console.log(
      `Scheduling SSE reconnection in ${delay}ms for visit ${visitId} (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`,
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      connect();
    }, delay);
  };

  const disconnect = () => {
    if (eventSourceRef.current) {
      console.log("Disconnecting SSE for visit:", visitId);
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setIsConnected(false);
  };

  useEffect(() => {
    if (visitId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [visitId, sseUrl]);

  return {
    isConnected,
    error,
    connect,
    disconnect,
    messageVisitId,
  };
};
